import { Copy06Icon } from '@repo/studio-design-system-ui-icons/general/copy-06'
import { DotsVerticalIcon } from '@repo/studio-design-system-ui-icons/general/dots-vertical'
import { PlusIcon } from '@repo/studio-design-system-ui-icons/general/plus'
import { Trash01Icon } from '@repo/studio-design-system-ui-icons/general/trash-01'
import { MenuItem, MenuItemText } from '@repo/studio-components/menu-item'
import { Button } from '@repo/studio-design-system-ui-button'
import { PopOverContainer } from '@repo/studio-design-system-ui-pop-over-container'
import { BottomSheet } from '@repo/studio-design-system-ui-sheets/bottom-sheet'
import { useCopyToClipboard } from '@repo/studio-hooks-copy/use-copy-to-clipboard'
import { useIsCompactViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import { usePortalContainerRef } from 'primitives/portal/portal-container'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { tw } from 'utils/classnames'

import { useAidaChatCommands } from '../../stores/chat-commands'

export function AidaConversationMenu({
  conversationId,
  label,
  includeNewChat = false,
  reveal,
}: {
  conversationId: string | null
  label?: string
  includeNewChat?: boolean
  reveal: 'hover' | 'always'
}) {
  const { t } = useTranslation('components')
  const isCompact = useIsCompactViewport()
  const portalContainerRef = usePortalContainerRef()
  const [open, setOpen] = React.useState(false)
  const title = label
    ? t('studio.components.aida.chat_actions_for', { title: label })
    : t('studio.components.aida.chat_actions')

  if (isCompact) {
    return (
      <>
        <Button
          iconOnly
          variant="ghost"
          size={reveal === 'hover' ? 'xs' : 'sm'}
          aria-label={title}
          onClick={event => {
            event.stopPropagation()
            setOpen(true)
          }}
        >
          <DotsVerticalIcon />
        </Button>
        <BottomSheet.Root open={open} onOpenChange={setOpen}>
          <BottomSheet.Content
            container={portalContainerRef?.current}
            className="z-[80]"
            overlayClassName="z-[80]"
          >
            <BottomSheet.Header>
              <BottomSheet.Title>{title}</BottomSheet.Title>
            </BottomSheet.Header>
            <ConversationActions
              conversationId={conversationId}
              includeNewChat={includeNewChat}
              onDone={() => {
                setOpen(false)
              }}
            />
          </BottomSheet.Content>
        </BottomSheet.Root>
      </>
    )
  }

  return (
    <DesktopConversationMenu
      conversationId={conversationId}
      includeNewChat={includeNewChat}
      reveal={reveal}
      open={open}
      setOpen={setOpen}
      title={title}
    />
  )
}

function DesktopConversationMenu({
  conversationId,
  includeNewChat,
  reveal,
  open,
  setOpen,
  title,
}: {
  conversationId: string | null
  includeNewChat: boolean
  reveal: 'hover' | 'always'
  open: boolean
  setOpen: (open: boolean) => void
  title: string
}) {
  const trigger = (
    <Button
      iconOnly
      variant="ghost"
      size={reveal === 'hover' ? 'xs' : 'sm'}
      aria-label={title}
      className={
        reveal === 'hover'
          ? tw(
              'opacity-0 pointer-events-none',
              'group-hover/history-row:pointer-events-auto group-hover/history-row:opacity-100',
              'group-focus-within/history-row:pointer-events-auto group-focus-within/history-row:opacity-100',
              open && 'pointer-events-auto opacity-100',
            )
          : undefined
      }
      onClick={event => {
        event.stopPropagation()
      }}
    >
      <DotsVerticalIcon />
    </Button>
  )
  const actions = (
    <ConversationActions
      conversationId={conversationId}
      includeNewChat={includeNewChat}
      onDone={() => {
        setOpen(false)
      }}
    />
  )

  return (
    <PopOverContainer.Root open={open} onOpenChange={setOpen}>
      <PopOverContainer.Trigger>{trigger}</PopOverContainer.Trigger>
      <PopOverContainer.Content align="end" side="bottom" className="w-60">
        {actions}
      </PopOverContainer.Content>
    </PopOverContainer.Root>
  )
}

function ConversationActions({
  conversationId,
  includeNewChat,
  onDone,
}: {
  conversationId: string | null
  includeNewChat: boolean
  onDone: () => void
}) {
  const { t } = useTranslation('components')
  const copyToClipboard = useCopyToClipboard()
  const canStartNewChat = useAidaChatCommands(state => state.canStartNewChat)
  const onNewChat = useAidaChatCommands(state => state.onNewChat)
  const requestDelete = useAidaChatCommands(state => state.requestDelete)

  return (
    <div className="flex flex-col gap-1">
      <MenuItem
        disabled={!conversationId}
        onClick={() => {
          if (!conversationId) {
            return
          }
          copyToClipboard(conversationId, {
            toastMessage: t('studio.components.aida.conversation_id_copied'),
          })
          onDone()
        }}
      >
        <Copy06Icon />
        <MenuItemText>{t('studio.components.aida.copy_conversation_id')}</MenuItemText>
      </MenuItem>
      <MenuItem
        disabled={!conversationId}
        onClick={() => {
          if (!conversationId) {
            return
          }
          requestDelete(conversationId)
          onDone()
        }}
      >
        <Trash01Icon />
        <MenuItemText>{t('studio.components.aida.delete_chat')}</MenuItemText>
      </MenuItem>
      {includeNewChat ? (
        <MenuItem
          disabled={!canStartNewChat}
          onClick={() => {
            onNewChat()
            onDone()
          }}
        >
          <PlusIcon />
          <MenuItemText>{t('studio.components.aida.start_new_chat')}</MenuItemText>
        </MenuItem>
      ) : null}
    </div>
  )
}
